import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ADMIN } from "@/constants/testIds";
import { adminClient, setToken, getToken, formatApiErrorDetail } from "@/lib/adminApi";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (getToken()) {
    return <Navigate to="/admin/waitlist" replace />;
  }

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email.trim() || !form.password) {
      setError("Email and password are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await adminClient.post("/admin/login", {
        email: form.email.trim(),
        password: form.password,
      });
      setToken(data.access_token);
      navigate("/admin/waitlist", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={ADMIN.loginPage}
      className="min-h-screen bg-[hsl(var(--scrm-bg))] grain text-[hsl(var(--scrm-cream))] flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        <div className="text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 mb-4">
          ✦ Admin Access
        </div>
        <h1 className="font-display uppercase tracking-mega text-4xl md:text-5xl leading-[0.95] mb-2">
          Members<br />Console.
        </h1>
        <p className="font-body text-sm text-[hsl(var(--scrm-cream))]/60 mb-10">
          SCRM CLUB administrators only.
        </p>

        <form onSubmit={submit} className="space-y-2">
          <div>
            <label className="block text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 mb-1">
              Email
            </label>
            <input
              data-testid={ADMIN.loginEmail}
              type="email"
              autoComplete="username"
              value={form.email}
              onChange={update("email")}
              placeholder="admin@scrmclub.com"
              className="editorial-input"
            />
          </div>
          <div className="pt-6">
            <label className="block text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 mb-1">
              Password
            </label>
            <input
              data-testid={ADMIN.loginPassword}
              type="password"
              autoComplete="current-password"
              value={form.password}
              onChange={update("password")}
              placeholder="••••••••"
              className="editorial-input"
            />
          </div>

          {error && (
            <div
              data-testid={ADMIN.loginError}
              className="pt-6 font-body text-xs text-red-300/85 tracking-wide"
            >
              {error}
            </div>
          )}

          <div className="pt-10">
            <button
              type="submit"
              data-testid={ADMIN.loginSubmit}
              disabled={submitting}
              className="btn-cream font-body uppercase tracking-[0.22em] text-xs px-10 py-5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Authenticating…" : "Sign In →"}
            </button>
          </div>
        </form>

        <div className="mt-12 pt-6 border-t hairline text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/40">
          ← <a href="/" className="hover:text-[hsl(var(--scrm-cream))]/80 transition-colors">Back to site</a>
        </div>
      </div>
    </div>
  );
}
